<template>
  <div class="bf-auth">
    <!-- Painel de marca -->
    <div class="bf-auth__brand">
      <div class="bf-auth__brand-inner">
        <img
          class="bf-auth__brand-logo"
          src="/img/brand/bindfy_logo_new_white.png"
          alt="Bindfy"
        />
        <h2>Comece a integrar em minutos.</h2>
        <p>
          Crie sua conta e conecte seus sistemas com uma plataforma feita para
          escalar o fluxo dos seus dados.
        </p>
      </div>
      <div class="bf-auth__blob bf-auth__blob--1"></div>
      <div class="bf-auth__blob bf-auth__blob--2"></div>
    </div>

    <!-- Formulario -->
    <div class="bf-auth__form-wrap">
      <div class="bf-auth__card">
        <img
          class="bf-auth__logo-mobile"
          src="/img/brand/bindfy_logo_new_purple.png"
          alt="Bindfy"
        />
        <h1 class="bf-auth__title">Criar conta</h1>
        <p class="bf-auth__desc">Preencha os dados para começar</p>

        <form @submit.prevent="handleSubmit()">
          <div class="bf-field">
            <label>E-mail</label>
            <TextInput
              :label="'Digite o seu e-mail'"
              :type="'email'"
              v-model="signUpForm.login"
              :error-messages="
                v$.login.$errors ? v$.login.$errors[0]?.$message : undefined
              "
            />
          </div>

          <div class="bf-field">
            <label>Senha</label>
            <TextInput
              :label="'Digite a sua senha'"
              :type="'password'"
              v-model="signUpForm.password"
              :error-messages="
                v$.password.$errors
                  ? v$.password.$errors[0]?.$message
                  : undefined
              "
            />
            <ul class="password-tips">
              <li :class="{ passed: signUpForm.password.length >= 6 }">
                Pelo menos 6 caracteres
              </li>
              <li :class="{ passed: /[A-Z]/.test(signUpForm.password) }">
                Letra maiúscula
              </li>
              <li :class="{ passed: /[a-z]/.test(signUpForm.password) }">
                Letra minúscula
              </li>
              <li :class="{ passed: /\d/.test(signUpForm.password) }">Número</li>
              <li
                :class="{
                  passed: /['!@#$%^&*(),.?':{}|<>]/.test(signUpForm.password),
                }"
              >
                Caractere especial
              </li>
            </ul>
          </div>

          <div class="bf-field">
            <label>Confirme a senha</label>
            <TextInput
              :label="'Digite a sua senha novamente'"
              :type="'password'"
              v-model="signUpForm.confirmationPassword"
              :error-messages="
                v$.confirmationPassword.$errors
                  ? v$.confirmationPassword.$errors[0]?.$message
                  : undefined
              "
            />
            <span v-if="passwordMismatch" class="bf-auth__error">
              As senhas não coincidem.
            </span>
          </div>

          <ul v-if="passwordSuggestions" class="password-suggestions">
            <li v-for="(suggestion, idx) in passwordSuggestions" :key="idx">
              {{ suggestion }}
            </li>
          </ul>

          <PrimaryButton
            :is-loading="isLoading"
            :disabled="passwordMismatch"
            label="Criar conta"
          />

          <div class="bf-auth__signup">
            Já possui uma conta?
            <router-link to="/autenticar/acessar" class="bf-link">
              Faça o login
            </router-link>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed } from "vue";

import { useRouter } from "vue-router";

import { useVuelidate } from "@vuelidate/core";

import { useAuth } from "@/composables/useAuth";
import { useAuthStore } from "@/stores/auth.store";


import PrimaryButton from "@/components/Forms/BasicElements/Button/PrimaryButton.vue";

import TextInput from "@/components/Forms/BasicElements/Input/TextInput.vue";

export default defineComponent({
  name: "SignUpPage",
  components: {
    TextInput,
    PrimaryButton,
  },
  setup() {
    const router = useRouter();

    const { signUpFormRules, signUpForm, signUp, isLoading, passwordSuggestions } = useAuth();

    const passwordMismatch = computed(
      () =>
        signUpForm.password &&
        signUpForm.confirmationPassword &&
        signUpForm.password !== signUpForm.confirmationPassword
    );


    const handleSubmit = async () => {
      try {
        const result = await v$.value.$validate();

        if (!result) return;

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { confirmationPassword, ...signUpPayload } = signUpForm;

        passwordSuggestions.value = useAuthStore().getPasswordSuggestions;

        const response = await signUp(signUpPayload);

        if (response) {
          router.push({ name: "SignInPage" });
        }
      } catch (e) {
        console.error("Ocorreu um erro ao conectar-se.");
      }
    };

    const v$ = useVuelidate(signUpFormRules, signUpForm);

    return {
      signUpForm,
      isLoading,
      handleSubmit,
      passwordMismatch,
      passwordSuggestions,
      v$,
    };
  },
});
</script>

<style lang="scss" scoped>
.password-tips {
  font-size: 12px;
  color: var(--text3);
  margin: 8px 0 4px 0;
  padding-left: 18px;
}
.password-tips .passed {
  color: var(--ok-fg);
  font-weight: 700;
}

.password-suggestions {
  color: var(--warn-fg);
  font-size: 12.5px;
  margin: 6px 0 0 0;
  padding-left: 18px;
}
.password-suggestions li {
  margin-bottom: 2px;
}
</style>
